import { ReactElement, FC } from "react";

import {
  Flex,
  HStack,
  ModalOverlay,
  Modal as ChakraModal,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ModalProps,
  SystemStyleObject,
} from "@chakra-ui/react";
import { theme } from "@ovou-inc/ovou-ui";
import { Text } from "../Typography";

export interface IModalProps extends Pick<
  ModalProps,
  "isOpen" | "onClose" | "onEsc" | "onOverlayClick"
> {
  title: ReactElement | string;
  modalBody: ReactElement;
  modalFooter?: ReactElement;
  dismissible?: boolean;
  size?: string;
  subtitle?: string;
  borderRadius?: string;
  bodyStyle?: SystemStyleObject;
  modalContentStyle?: SystemStyleObject;
  modalHeaderStyle?: SystemStyleObject;
  closeButtonStyle?: SystemStyleObject;
  modalFooterStyle?: SystemStyleObject;
  isCentered?: boolean;
  motionPreset?: ModalProps["motionPreset"];
  initialFocusRef?: ModalProps["initialFocusRef"];
  // `"responsive"` opts into the desktop-capped shell shared by the
  // profile-templates modals: 552px max with a 32px mobile gutter,
  // responsive horizontal/vertical padding, and a soft elevation shadow.
  // Callers can still narrow `maxWidth` via `modalContentStyle`.
  variant?: "default" | "responsive";
}

const RESPONSIVE_SHELL_STYLE: SystemStyleObject = {
  maxWidth: "552px",
  width: "calc(100vw - 32px)",
  paddingX: { base: "20px", md: "32px" },
  paddingY: { base: "20px", md: "32px" },
  boxShadow: "0px 6px 20px 0px rgba(0, 0, 0, 0.12)",
};

const DEFAULT_SHELL_STYLE: SystemStyleObject = {
  maxWidth: "lg",
  paddingX: "22px",
};

const NewModal: FC<IModalProps> = ({
  isOpen,
  onClose,
  title,
  dismissible = true,
  onEsc,
  onOverlayClick,
  modalBody: ModalBodyContent,
  modalFooter: ModalFooterContent,
  size,
  subtitle,
  borderRadius,
  bodyStyle,
  modalContentStyle,
  modalHeaderStyle,
  closeButtonStyle,
  modalFooterStyle,
  isCentered,
  motionPreset = "slideInBottom",
  initialFocusRef,
  variant = "default",
}) => {
  // Default positioning is fixed/top-anchored (legacy); switch to centered/relative
  // when the caller asks via `isCentered`.
  const positionalStyle: SystemStyleObject = isCentered
    ? { position: "relative", top: "auto", bottom: "auto", my: "auto" }
    : { position: "fixed", bottom: "inherit", top: "20%" };

  const shellStyle: SystemStyleObject =
    variant === "responsive" ? RESPONSIVE_SHELL_STYLE : DEFAULT_SHELL_STYLE;

  return (
    <ChakraModal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={dismissible}
      closeOnOverlayClick={dismissible}
      motionPreset={motionPreset}
      onEsc={onEsc}
      onOverlayClick={onOverlayClick}
      scrollBehavior="inside"
      size={size}
      isCentered={isCentered}
      initialFocusRef={initialFocusRef}
      returnFocusOnClose={false}
    >
      <ModalOverlay />
      <ModalContent
        sx={{
          ...shellStyle,
          ...positionalStyle,
          borderRadius: `${borderRadius ? borderRadius : "16px"}`,
          ...(modalContentStyle ?? {}),
        }}
      >
        <ModalHeader
          sx={{
            color: theme.palette.brand.primary.black,
            lineHeight: 1.25,
            letterSpacing: "-0.24px",
            ...modalHeaderStyle,
          }}
        >
          <Flex direction="column" gap="8px">
            {title}
            {subtitle && (
              <Text
                fontSize="16px"
                fontWeight={400}
                lineHeight={1.5}
                letterSpacing="normal"
                color={theme.palette.ui.greys.grey1}
              >
                {subtitle}
              </Text>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton _focus={{ outline: "none" }} sx={closeButtonStyle} />
        <ModalBody
          sx={{
            pb: ModalFooterContent ? 0 : 4,
            ...(typeof bodyStyle === "object" ? bodyStyle : {}),
          }}
        >
          {ModalBodyContent}
        </ModalBody>

        {ModalFooterContent && (
          <ModalFooter sx={modalFooterStyle}>
            <HStack w="full" spacing="2">
              {ModalFooterContent}
            </HStack>
          </ModalFooter>
        )}
      </ModalContent>
    </ChakraModal>
  );
};

export default NewModal;
